import { useEffect, useState, useCallback } from "react";
import AppLayout from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, Filter, Calendar as CalIcon, TrendingUp, DollarSign, RefreshCw, AlertTriangle, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import SettleDebtDialog from "@/components/SettleDebtDialog";
import { useAuth } from "@/context/AuthContext";
import useAutoRefresh from "@/lib/useAutoRefresh";

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

/* ---------- Accounting week helper ---------- */
// The accounting week runs from Friday 00:00 (midnight, Naples EDT) through
// the following Thursday 23:59. Since dispatchers operate in Naples FL, the
// browser's local timezone typically already matches America/New_York.
function accountingWeekStart(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay();     // 0=Sun … 5=Fri
  const daysBack = (day - 5 + 7) % 7;
  const r = new Date(d);
  r.setDate(d.getDate() - daysBack);
  r.setHours(0, 0, 0, 0);
  return r;
}

function startOfDay(d = new Date()) {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
}

// Local-time datetime picker value (yyyy-MM-ddTHH:mm)
function toLocalDT(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalDT(s) { return s ? new Date(s) : null; }

// Nice human label for the selected range
function labelFor(preset, startD, endD) {
  const fmtDate = (d) =>
    d.toLocaleDateString("es-ES", { weekday: "short", day: "2-digit", month: "short" });
  const fmtTime = (d) => d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
  if (preset === "today") return "Hoy · desde 12:00 AM hasta ahora";
  if (preset === "yesterday") return "Ayer · día completo";
  if (preset === "week") return `Semana contable · ${fmtDate(startD)} ${fmtTime(startD)} → ahora`;
  if (preset === "prevweek") return `Semana anterior · ${fmtDate(startD)} ${fmtTime(startD)} → ${fmtDate(endD)} ${fmtTime(endD)}`;
  return `Rango personalizado · ${fmtDate(startD)} → ${fmtDate(endD)}`;
}

/* ---------- Component ---------- */
export default function Reports() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const now = new Date();
  const [preset, setPresetState] = useState("today");
  // Store dates as ISO datetime local strings (used by <input type="datetime-local">)
  const [startLocal, setStartLocal] = useState(toLocalDT(startOfDay(now)));
  const [endLocal, setEndLocal] = useState(toLocalDT(now));
  const [grouping, setGrouping] = useState("driver");
  const [driverId, setDriverId] = useState("all");
  const [drivers, setDrivers] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [settleFor, setSettleFor] = useState(null); // {id, name, outstanding}
  const [showOrphans, setShowOrphans] = useState(false);

  const applyPreset = useCallback((p) => {
    const t = new Date();
    if (p === "today") {
      setStartLocal(toLocalDT(startOfDay(t)));
      setEndLocal(toLocalDT(t));
      setGrouping("driver");
    } else if (p === "yesterday") {
      const y = new Date(t); y.setDate(t.getDate() - 1);
      const s = startOfDay(y);
      const e = new Date(y); e.setHours(23, 59, 59, 0);
      setStartLocal(toLocalDT(s));
      setEndLocal(toLocalDT(e));
      setGrouping("driver");
    } else if (p === "week") {
      const s = accountingWeekStart(t);
      setStartLocal(toLocalDT(s));
      setEndLocal(toLocalDT(t));
      setGrouping("driver");
    } else if (p === "prevweek") {
      const currentStart = accountingWeekStart(t);
      const prevStart = new Date(currentStart);
      prevStart.setDate(currentStart.getDate() - 7);
      setStartLocal(toLocalDT(prevStart));
      setEndLocal(toLocalDT(currentStart));
      setGrouping("driver");
    }
    setPresetState(p);
  }, []);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const s = fromLocalDT(startLocal);
      const e = fromLocalDT(endLocal);
      if (!s || !e) return;
      const params = new URLSearchParams({
        start: s.toISOString(),
        end: e.toISOString(),
        grouping,
      });
      if (driverId && driverId !== "all") params.set("driver_id", driverId);
      const { data } = await api.get(`/reports?${params.toString()}`);
      setData(data);
      setLastRefreshed(new Date());
    } catch (e) {
      if (!silent) toast.error(formatApiError(e));
    } finally { if (!silent) setLoading(false); }
  }, [startLocal, endLocal, grouping, driverId]);

  useEffect(() => {
    api.get("/users/drivers").then(({ data }) => setDrivers(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh silently every 15s + on tab focus, so settlements or trips
  // registered elsewhere show up without manual reload.
  useAutoRefresh(() => load({ silent: true }), 15000);

  const exportCSV = () => {
    if (!data) return;
    const cols = ["driver_name", "trips", "driver_income", "total_income", "llc_income", "cash", "zelle", "driver_collected", "client_debt_payment", "driver_investment", "driver_to_llc", "settlements_paid", "outstanding_debt"];
    const header = ["Chofer", "Viajes", "Ingreso chofer", "Total facturado", "LLC Income", "Cash", "Zelle", "Cobrado Chofer", "Pago deuda", "Inversión", "Driver→LLC", "Liquidado", "Deuda pendiente"];
    const lines = [header.join(",")];
    data.rows.forEach((r) => lines.push(cols.map((c) => JSON.stringify(r[c] ?? "")).join(",")));
    lines.push(cols.map((c) => JSON.stringify(data.total[c] ?? "")).join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `reporte-${preset}-${startLocal}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadWeeklyPDF = async () => {
    // Uses the START-date field as the anchor so the dispatcher can pick any
    // day of any past week; the backend snaps it to that week's Friday.
    const anchorDate = startLocal ? startLocal.slice(0, 10) : "";
    try {
      const res = await api.get(
        `/reports/weekly-pdf${anchorDate ? `?friday_of_week=${anchorDate}` : ""}`,
        { responseType: "blob" },
      );
      const cd = res.headers?.["content-disposition"] || "";
      const match = /filename="([^"]+)"/.exec(cd);
      const filename = match ? match[1] : "reporte-semanal.pdf";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("PDF semanal generado");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const recomputeSplits = async () => {
    const s = fromLocalDT(startLocal);
    const e = fromLocalDT(endLocal);
    if (!s || !e) return;
    const startISO = s.toISOString().slice(0, 10);
    const endISO = e.toISOString().slice(0, 10);
    const scope = driverId && driverId !== "all"
      ? (drivers.find((d) => d.id === driverId)?.name || "el chofer seleccionado")
      : "TODOS los choferes";
    if (!window.confirm(
      `Recalcular la comisión de ${scope} para viajes entre ${startISO} y ${endISO}?\n\n` +
      `Se usará el driver_pct ACTUAL de cada chofer. No cambia inversión ni recibos.`
    )) return;
    try {
      const body = { start: startISO, end: endISO };
      if (driverId && driverId !== "all") body.driver_id = driverId;
      const { data: res } = await api.post("/trips/recompute-splits", body);
      const skipped = Number(res?.skipped_orphans || 0);
      if (skipped > 0) {
        toast.warning(
          `Recomputo: ${res.updated} viajes actualizados de ${res.scanned}. ${skipped} viaje${skipped !== 1 ? "s" : ""} saltado${skipped !== 1 ? "s" : ""} por chofer huérfano — revisa "Viajes inconsistentes".`,
          { duration: 8000 }
        );
      } else {
        toast.success(`Recomputo listo: ${res.updated} viajes actualizados de ${res.scanned} escaneados`);
      }
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const startDate = fromLocalDT(startLocal);
  const endDate = fromLocalDT(endLocal);
  const rangeLabel = startDate && endDate ? labelFor(preset, startDate, endDate) : "";

  const presets = [
    { key: "today",     label: "Hoy" },
    { key: "yesterday", label: "Ayer" },
    { key: "week",      label: "Semana contable" },
    { key: "prevweek",  label: "Semana anterior" },
    { key: "custom",    label: "Personalizado" },
  ];

  return (
    <AppLayout>
      <div className="p-6 md:p-8 space-y-6 max-w-7xl mx-auto w-full">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-mono text-primary tracking-widest">REPORTS</div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight" style={{ fontFamily: "Manrope" }}>
              Ingresos por chofer
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">
              La semana contable empieza los <span className="text-white font-mono">Viernes a las 12:00 AM</span> (Naples EDT).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-[10px] font-mono text-muted-foreground mr-1 tabular-nums hidden md:block" data-testid="reports-last-refreshed">
              {lastRefreshed
                ? `actualizado ${lastRefreshed.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })}`
                : "cargando…"}
            </div>
            <Button
              onClick={() => load()}
              variant="outline"
              size="icon"
              className="border-border/60 hover:bg-secondary/60"
              disabled={loading}
              data-testid="reports-refresh"
              title="Actualizar ahora"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            {isAdmin && (
              <Button
                onClick={recomputeSplits}
                variant="outline"
                className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 font-bold"
                data-testid="reports-recompute-splits"
                title="Recalcula driver_income / llc_income / driver_collected usando el driver_pct actual del chofer y el payment_method del viaje. Útil tras cambiar la comisión de un chofer."
              >
                <RefreshCw className="w-4 h-4 mr-1" /> Recomputar split
              </Button>
            )}
            <Button
              onClick={downloadWeeklyPDF}
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold"
              data-testid="reports-weekly-pdf"
              title="Descargar el reporte semanal (viernes → jueves) que contiene la fecha inicial seleccionada."
            >
              <FileText className="w-4 h-4 mr-1" /> PDF semanal
            </Button>
            <Button onClick={exportCSV} disabled={!data} className="bg-accent text-accent-foreground hover:bg-accent/90 font-bold" data-testid="reports-export-csv">
              <Download className="w-4 h-4 mr-1" /> Exportar CSV
            </Button>
          </div>
        </div>

        {/* Presets */}
        <Card className="bg-card border-border p-4 space-y-4">
          <div className="flex flex-wrap gap-2">
            {presets.map((p) => (
              <button
                key={p.key}
                onClick={() => applyPreset(p.key)}
                data-testid={`reports-preset-${p.key}`}
                className={`text-sm font-bold px-4 py-2 rounded-md border transition-colors ${
                  preset === p.key
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border text-muted-foreground hover:text-white hover:border-white/40"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Range banner */}
          <div className="flex items-center gap-2 text-xs font-mono text-primary bg-primary/10 border border-primary/30 rounded-md px-3 py-2" data-testid="reports-range-label">
            <CalIcon className="w-3.5 h-3.5" />
            {rangeLabel}
          </div>

          {/* Advanced filters (only visible on custom or as override) */}
          {preset === "custom" && (
            <div className="grid md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Desde</Label>
                <Input type="datetime-local" value={startLocal} onChange={(e) => setStartLocal(e.target.value)} data-testid="reports-start" className="bg-input border-border" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Hasta</Label>
                <Input type="datetime-local" value={endLocal} onChange={(e) => setEndLocal(e.target.value)} data-testid="reports-end" className="bg-input border-border" />
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground flex items-center gap-1"><Filter className="w-3 h-3" /> Chofer</Label>
              <Select value={driverId} onValueChange={setDriverId}>
                <SelectTrigger className="bg-input border-border" data-testid="reports-driver-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los choferes</SelectItem>
                  {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Agrupación</Label>
              <Select value={grouping} onValueChange={setGrouping}>
                <SelectTrigger className="bg-input border-border" data-testid="reports-grouping"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="driver">Total por chofer</SelectItem>
                  <SelectItem value="day">Por día</SelectItem>
                  <SelectItem value="week">Por semana</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* KPI: emphasis on driver income */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="p-5 bg-gradient-to-br from-emerald-500/15 to-emerald-500/5 border-emerald-500/40 relative overflow-hidden col-span-2">
              <TrendingUp className="absolute top-3 right-3 w-6 h-6 text-emerald-400 opacity-50" />
              <div className="text-[10px] uppercase tracking-widest text-emerald-400/80 font-mono mb-1">
                Ingreso real de los choferes
              </div>
              <div className="text-4xl md:text-5xl font-black font-mono text-emerald-400" data-testid="kpi-driver-income">
                {fmt(data.total.driver_income)}
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                {data.total.trips} viajes · de {fmt(data.total.total_income)} facturados
              </div>
            </Card>
            <KpiTile label="LLC Income" value={fmt(data.total.llc_income)} accent="text-cyan-400" />
            <KpiTile label="Cash cobrado" value={fmt(data.total.driver_collected)} accent="text-amber-400" />
          </div>
        )}

        {/* Orphan / data-corruption warning banner */}
        {data && Array.isArray(data.orphans) && data.orphans.length > 0 && (
          <Card
            className="bg-red-500/10 border-red-500/50 p-4 flex items-start gap-3"
            data-testid="orphan-banner"
          >
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-red-300">
                {data.orphans.length} viaje{data.orphans.length !== 1 ? "s" : ""} con datos inconsistentes
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Chofer huérfano, split que no cuadra con el precio o cobrado incorrecto para Zelle. Estos viajes ensucian los totales y hacen que "Recomputar split" no funcione bien.
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="border-red-500/50 text-red-300 hover:bg-red-500/10 font-bold flex-shrink-0"
              onClick={() => setShowOrphans(true)}
              data-testid="orphan-banner-view"
            >
              Ver viajes
            </Button>
          </Card>
        )}

        {/* Table */}
        <Card className="bg-card border-border overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border">
                  <TableHead>Chofer</TableHead>
                  {grouping !== "driver" && <TableHead>{grouping === "week" ? "Semana" : "Día"}</TableHead>}
                  <TableHead className="text-right">Viajes</TableHead>
                  <TableHead className="text-right font-bold text-emerald-400">Ingreso chofer</TableHead>
                  <TableHead className="text-right">Total facturado</TableHead>
                  <TableHead className="text-right">LLC</TableHead>
                  <TableHead className="text-right">Cash</TableHead>
                  <TableHead className="text-right">Zelle</TableHead>
                  <TableHead className="text-right">Cobrado</TableHead>
                  <TableHead className="text-right" title="Efectivo cobrado por deudas viejas de clientes — va 100% a LLC además del split normal">Pago deuda</TableHead>
                  <TableHead className="text-right">Inversión</TableHead>
                  <TableHead className="text-right">Driver→LLC</TableHead>
                  <TableHead className="text-right text-emerald-400">Liquidado</TableHead>
                  <TableHead className="text-right">Deuda pendiente</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow className="border-border"><TableCell colSpan={14} className="text-center text-muted-foreground py-8">Cargando...</TableCell></TableRow>
                )}
                {!loading && (!data || data.rows.length === 0) && (
                  <TableRow className="border-border"><TableCell colSpan={14} className="text-center text-muted-foreground py-12">
                    Sin viajes completados en este rango.
                  </TableCell></TableRow>
                )}
                {data?.rows.map((r, i) => (
                  <TableRow key={`${r.driver_id}-${r.bucket}-${i}`} className="border-border" data-testid={`report-row-${i}`}>
                    <TableCell className="font-medium">{r.driver_name}</TableCell>
                    {grouping !== "driver" && <TableCell className="font-mono text-xs">{r.bucket}</TableCell>}
                    <TableCell className="text-right font-mono">{r.trips}</TableCell>
                    <TableCell className="text-right font-mono font-bold text-emerald-400 text-base">{fmt(r.driver_income)}</TableCell>
                    <TableCell className="text-right font-mono text-amber-400">{fmt(r.total_income)}</TableCell>
                    <TableCell className="text-right font-mono text-cyan-400">{fmt(r.llc_income)}</TableCell>
                    <TableCell className="text-right font-mono">{fmt(r.cash)}</TableCell>
                    <TableCell className="text-right font-mono">{fmt(r.zelle)}</TableCell>
                    <TableCell className="text-right font-mono">{fmt(r.driver_collected)}</TableCell>
                    <TableCell className={`text-right font-mono ${(r.client_debt_payment || 0) > 0.005 ? "text-amber-400 font-bold" : ""}`} title="Efectivo que un cliente pagó por deuda vieja">{fmt(r.client_debt_payment || 0)}</TableCell>
                    <TableCell className="text-right font-mono text-destructive">{fmt(r.driver_investment)}</TableCell>
                    <TableCell className="text-right font-mono">
                      {r.driver_to_llc < -0.005 ? (
                        <span className="text-emerald-400 font-bold" title="Empresa le debe al chofer">
                          Empresa debe {fmt(Math.abs(r.driver_to_llc))}
                        </span>
                      ) : (
                        fmt(r.driver_to_llc)
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono text-emerald-400">{fmt(r.settlements_paid)}</TableCell>
                    <TableCell className="text-right font-mono">
                      <div className="flex items-center justify-end gap-2">
                        <span className={`font-bold ${r.outstanding_debt > 0.005 ? "text-red-400" : "text-muted-foreground"}`}>
                          {fmt(r.outstanding_debt)}
                        </span>
                        {grouping === "driver" && r.driver_id && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 border border-emerald-500/30 hover:bg-emerald-500/10 hover:text-emerald-400"
                            onClick={() => setSettleFor({
                              id: r.driver_id,
                              name: r.driver_name,
                              outstanding: r.outstanding_debt,
                            })}
                            data-testid={`settle-btn-${r.driver_id}`}
                            title="Liquidar deuda de este chofer"
                          >
                            <DollarSign className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {data && data.rows.length > 0 && (
                  <TableRow className="border-border bg-secondary/40" data-testid="report-total-row">
                    <TableCell className="font-bold uppercase text-xs tracking-widest">Total</TableCell>
                    {grouping !== "driver" && <TableCell></TableCell>}
                    <TableCell className="text-right font-mono font-bold">{data.total.trips}</TableCell>
                    <TableCell className="text-right font-mono font-bold text-emerald-400 text-base">{fmt(data.total.driver_income)}</TableCell>
                    <TableCell className="text-right font-mono font-bold text-amber-400">{fmt(data.total.total_income)}</TableCell>
                    <TableCell className="text-right font-mono font-bold text-cyan-400">{fmt(data.total.llc_income)}</TableCell>
                    <TableCell className="text-right font-mono font-bold">{fmt(data.total.cash)}</TableCell>
                    <TableCell className="text-right font-mono font-bold">{fmt(data.total.zelle)}</TableCell>
                    <TableCell className="text-right font-mono font-bold">{fmt(data.total.driver_collected)}</TableCell>
                    <TableCell className={`text-right font-mono font-bold ${(data.total.client_debt_payment || 0) > 0.005 ? "text-amber-400" : ""}`}>{fmt(data.total.client_debt_payment || 0)}</TableCell>
                    <TableCell className="text-right font-mono font-bold text-destructive">{fmt(data.total.driver_investment)}</TableCell>
                    <TableCell className="text-right font-mono font-bold">
                      {data.total.driver_to_llc < -0.005 ? (
                        <span className="text-emerald-400">
                          Empresa debe {fmt(Math.abs(data.total.driver_to_llc))}
                        </span>
                      ) : (
                        fmt(data.total.driver_to_llc)
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono font-bold text-emerald-400">{fmt(data.total.settlements_paid)}</TableCell>
                    <TableCell className={`text-right font-mono font-bold ${data.total.outstanding_debt > 0.005 ? "text-red-400" : ""}`}>
                      {fmt(data.total.outstanding_debt)}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
      <SettleDebtDialog
        open={!!settleFor}
        onOpenChange={(v) => !v && setSettleFor(null)}
        driverId={settleFor?.id}
        driverName={settleFor?.name}
        outstanding={settleFor?.outstanding || 0}
        periodStart={startDate?.toISOString()}
        periodEnd={endDate?.toISOString()}
        onSaved={load}
      />

      <Dialog open={showOrphans} onOpenChange={setShowOrphans}>
        <DialogContent
          className="bg-card border-border max-w-3xl max-h-[90vh] flex flex-col p-0 gap-0"
          data-testid="orphan-dialog"
        >
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-border/50 flex-shrink-0">
            <DialogTitle style={{ fontFamily: "Manrope" }} className="text-2xl flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-red-400" /> Viajes con datos inconsistentes
            </DialogTitle>
            <div className="text-xs text-muted-foreground pt-1">
              Editá cada viaje desde Historial para reasignar el chofer correcto, ajustar el precio o cambiar el método de pago — al guardar el sistema recalcula los splits automáticamente.
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2" data-testid="orphan-list">
            {(data?.orphans || []).map((o) => (
              <OrphanRow key={o.trip_id} orphan={o} />
            ))}
            {(data?.orphans || []).length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-6">
                No hay viajes inconsistentes en este rango.
              </div>
            )}
          </div>
          <DialogFooter className="px-6 py-4 border-t border-border/50 flex-shrink-0 bg-card">
            <Button variant="outline" onClick={() => setShowOrphans(false)} data-testid="orphan-close">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

const ORPHAN_REASON_LABEL = {
  sin_driver_id: "Sin chofer asignado",
  driver_no_existe: "Chofer borrado o inválido",
  split_inconsistente: "Split no cuadra con el precio",
  cobrado_zelle_no_cero: "Zelle con Cobrado > 0",
};

function OrphanRow({ orphan }) {
  const reasonLabel = (r) => ORPHAN_REASON_LABEL[r] || r;
  const splitSum = Number((orphan.driver_income + orphan.llc_income).toFixed(2));
  return (
    <div
      className="rounded-lg border border-red-500/40 bg-red-500/5 p-3 flex flex-col gap-2"
      data-testid={`orphan-row-${orphan.trip_id}`}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-xs px-2 py-0.5 rounded bg-secondary/60 text-white">
            {orphan.trip_number || "sin nº"}
          </span>
          <span className="text-sm font-bold">{orphan.driver_name}</span>
          <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            {orphan.payment_method}
          </span>
        </div>
        <div className="text-right">
          <div className="font-mono text-sm font-bold text-amber-400">
            ${orphan.price.toFixed(2)}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {orphan.reasons.map((r) => (
          <span
            key={r}
            className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-300 border border-red-500/40 font-bold"
          >
            {reasonLabel(r)}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2 text-[11px] font-mono">
        <div>
          <div className="text-muted-foreground text-[9px] uppercase tracking-widest">Chofer</div>
          <div className="text-emerald-400">${orphan.driver_income.toFixed(2)}</div>
        </div>
        <div>
          <div className="text-muted-foreground text-[9px] uppercase tracking-widest">LLC</div>
          <div className="text-cyan-400">${orphan.llc_income.toFixed(2)}</div>
        </div>
        <div>
          <div className="text-muted-foreground text-[9px] uppercase tracking-widest">
            Chofer + LLC {splitSum !== orphan.price && (
              <span className="text-red-400">≠ precio</span>
            )}
          </div>
          <div className={splitSum !== orphan.price ? "text-red-400" : "text-white"}>
            ${splitSum.toFixed(2)}
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiTile({ label, value, accent = "text-white" }) {
  return (
    <Card className="p-4 bg-card border-border">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono mb-1">{label}</div>
      <div className={`text-2xl md:text-3xl font-black font-mono ${accent}`}>{value}</div>
    </Card>
  );
}
